import {AxiosInstance, AxiosRequestConfig} from "axios";
import download = require("downloadjs");

/**
 * A bill as entered so far. Every field but the brand and the MPRN is optional, so the form can ask while it is still
 * being filled in.
 */
export interface EvChargingBillCheckRequest {
	partnerBrandId: string;
	mprn: string;
	periodFrom?: string;
	periodTo?: string;
	kwh?: number;
}

/**
 * One reason the bill cannot be saved as entered, or one thing worth knowing that does not stop it. `field` names the
 * form field to show it on: `mprn`, `periodFrom`, `periodTo` or `kwh`.
 */
export interface EvChargingBillMessage {
	code: string;
	field: string;
	message: string;
}

export interface EvChargingSimilarMeterPoint {
	id: string;
	mprn: string;
	nickname?: string;
}

export interface EvChargingBillCheck {
	meterPointId?: string;
	createsMeterPoint: boolean;
	similarMeterPoints: EvChargingSimilarMeterPoint[];
	year?: number;
	quarter?: number;
	submissionDeadline?: string;
	credits?: number;
	problems: EvChargingBillMessage[];
	warnings: EvChargingBillMessage[];
}

/**
 * The job is raised against the partner brand's default contact; the backend creates it if the brand has none.
 */
export interface CreateEvChargingJobRequest {
	partnerBrandId: string;
	mprn: string;
	periodFrom: string;
	periodTo: string;
	kwh: number;
	billAttachmentIds: string[];
}

export interface CreatedEvChargingJob {
	id: string;
	meterPointId: string;
	year: number;
	quarter: number;
	credits: number;
	warnings: EvChargingBillMessage[];
}

export interface EvChargingMeterPoint {
	id: string;
	mprn: string;
	partnerBrandId: string;
	nickname?: string;
	dedicatedToEvCharging: boolean;
	billCount: number;
	lastBillPeriodFrom?: string;
	lastBillPeriodTo?: string;
}

/**
 * `dedicatedToEvCharging` has no default on purpose: whoever adds the meter answers it.
 */
export interface AddEvChargingMeterPointRequest {
	mprn: string;
	dedicatedToEvCharging: boolean;
	nickname?: string;
}

/**
 * The body of the 409 answering a meter added under an MPRN some brand already holds, this one included.
 */
export interface EvChargingMeterPointTaken {
	message: string;
	meterPointId: string;
	mprn: string;
	heldByPartnerBrandId: string;
	heldByPartnerBrandName: string;
}

/**
 * Where a quarter stands on its NORA submission: still running, over with the claim due by the deadline (inclusive),
 * or past the deadline. Informative only: no state stops a bill being added.
 */
export type EvChargingQuarterState = "IN_PROGRESS" | "SUBMISSION_WINDOW_OPEN" | "SUBMISSION_WINDOW_CLOSED";

/**
 * Totals are summed from the quarter's bills on every request, the same way as the Statement of Supply.
 */
export interface EvChargingQuarterSummary {
	id: string;
	year: number;
	quarter: number;
	firstDay: string;
	lastDay: string;
	submissionDeadline: string;
	state: EvChargingQuarterState;
	totalKwh: number;
	totalCredits: number;
	billCount: number;
	/**
	 * Bills entered after the NORA deadline: they change totals that have most likely been submitted already.
	 */
	lateBillCount: number;
	meterPointCount: number;
}

export interface EvChargingQuarterBill {
	jobId: string;
	jobReferenceNumber: string;
	partnerBrandId: string;
	periodFrom: string;
	periodTo: string;
	kwh: number;
	credits: number;
	enteredOn: string;
	late: boolean;
	files: { attachmentId: string; name: string }[];
}

/**
 * One row of a month's Statement of Supply. A meter moved to another partner brand during the month lists both.
 */
export interface EvChargingQuarterMeterPoint {
	meterPointId: string;
	mprn: string;
	nickname?: string;
	partnerBrands: { id: string; name: string }[];
	totalKwh: number;
	totalCredits: number;
	bills: EvChargingQuarterBill[];
}

/**
 * One month of the quarter, cut the same way as its Statement of Supply: NORA's portal takes a file per month.
 */
export interface EvChargingQuarterMonth {
	/**
	 * As "2026-07", the form the month's download takes.
	 */
	month: string;
	firstDay: string;
	lastDay: string;
	totalKwh: number;
	totalCredits: number;
	billCount: number;
	lateBillCount: number;
	meterPointCount: number;
	meterPoints: EvChargingQuarterMeterPoint[];
}

export interface EvChargingQuarter {
	summary: EvChargingQuarterSummary;
	meterPoints: EvChargingQuarterMeterPoint[];
	/**
	 * Only months with bills, in order, the same as the files in the quarter's ZIP.
	 */
	months: EvChargingQuarterMonth[];
}

/**
 * EV charging credits in the back office: bills of meters dedicated to EV charging, and the meters themselves.
 */
export default class EvChargingApi {
	constructor(private readonly axiosInstance: AxiosInstance) {}

	/**
	 * What saving the bill as entered so far would do, by the same rules as creating it. Saves nothing and always
	 * answers with the problems listed rather than failing.
	 */
	checkBill(request: EvChargingBillCheckRequest): Promise<EvChargingBillCheck> {
		return this.axiosInstance.post("/api/web/ev-charging/jobs/check", request).then(resp => resp.data);
	}

	/**
	 * Fails with 422 and a message naming what to fix when the bill cannot be claimed as entered.
	 */
	createJob(request: CreateEvChargingJobRequest): Promise<CreatedEvChargingJob> {
		return this.axiosInstance.post("/api/web/ev-charging/jobs", request).then(resp => resp.data);
	}

	/**
	 * Meter points the partner brand holds, ordered by MPRN, each with its bill count and latest bill.
	 */
	getMeterPoints(partnerBrandId: string): Promise<EvChargingMeterPoint[]> {
		return this.axiosInstance
			.get(`/api/web/partner_brands/${encodeURIComponent(partnerBrandId)}/ev-charging/meter-points`)
			.then(resp => resp.data);
	}

	/**
	 * Fails with 409 and {@link EvChargingMeterPointTaken} when a brand already holds the MPRN, and with 422 when it is
	 * not 11 digits.
	 */
	addMeterPoint(partnerBrandId: string, request: AddEvChargingMeterPointRequest): Promise<EvChargingMeterPoint> {
		return this.axiosInstance
			.post(`/api/web/partner_brands/${encodeURIComponent(partnerBrandId)}/ev-charging/meter-points`, request)
			.then(resp => resp.data);
	}

	/**
	 * An empty name removes it.
	 */
	renameMeterPoint(meterPointId: string, nickname: string): Promise<EvChargingMeterPoint> {
		return this.axiosInstance
			.put(`/api/web/ev-charging/meter-points/${encodeURIComponent(meterPointId)}/nickname`,
				{nickname: nickname || null})
			.then(resp => resp.data);
	}

	/**
	 * Bills already entered stay with the brand that held the meter; only bills entered from now on go to the new one.
	 */
	moveMeterPoint(meterPointId: string, partnerBrandId: string): Promise<EvChargingMeterPoint> {
		return this.axiosInstance
			.put(`/api/web/ev-charging/meter-points/${encodeURIComponent(meterPointId)}/partner-brand`, {partnerBrandId})
			.then(resp => resp.data);
	}

	/**
	 * Quarters that have bills, newest first.
	 */
	getQuarters(): Promise<EvChargingQuarterSummary[]> {
		return this.axiosInstance.get("/api/web/ev-charging/quarters").then(resp => resp.data);
	}

	/**
	 * One quarter as a row per meter, each with its bills, for the whole quarter and for each of its months.
	 */
	getQuarter(quarterId: string): Promise<EvChargingQuarter> {
		return this.axiosInstance.get(`/api/web/ev-charging/quarters/${encodeURIComponent(quarterId)}`)
			.then(resp => resp.data);
	}

	/**
	 * Saves the quarter's Statements of Supply for NORA, a CSV per month with bills in one ZIP, generated as they
	 * stand now. Downloading closes nothing.
	 */
	downloadStatementOfSupply(quarter: EvChargingQuarterSummary): Promise<void> {
		const config: AxiosRequestConfig = {responseType: "blob", timeout: 60000};
		return this.axiosInstance
			.get(`/api/web/ev-charging/quarters/${encodeURIComponent(quarter.id)}/statement-of-supply`, config)
			.then(resp => {
				download(resp.data, `statement-of-supply-${quarter.year}-Q${quarter.quarter}.zip`, "application/zip");
			});
	}

	/**
	 * Saves one month's Statement of Supply, the same file as in the quarter's ZIP.
	 */
	downloadMonthStatementOfSupply(quarter: EvChargingQuarterSummary, month: string): Promise<void> {
		const config: AxiosRequestConfig = {responseType: "blob", timeout: 60000};
		return this.axiosInstance
			.get(`/api/web/ev-charging/quarters/${encodeURIComponent(quarter.id)}/statement-of-supply/${
				encodeURIComponent(month)}`, config)
			.then(resp => {
				download(resp.data, `statement-of-supply-${month}.csv`, "text/csv");
			});
	}
}
